import type { QueryClient } from "@tanstack/react-query";
import type { ComponentType, ReactNode } from "react";
import {
  Outlet,
  type LoaderFunction,
  type RouteObject,
  type ShouldRevalidateFunction,
} from "react-router";
import { adviceListQuery, adviceQuery } from "../features/advice/queries.ts";
import { landQuery, landsQuery } from "../features/lands/queries.ts";
import { calibrationsQuery, pipelineQuery, runnerKeysQuery } from "../features/lab/queries.ts";
import { researchListQuery, researchQuery } from "../features/research/queries.ts";
import { sampleQuery, samplesQuery } from "../features/samples/queries.ts";
import { useIsAdmin, useIsAnalyst } from "./current-user.tsx";
import { NoAccessPage, NotFoundPage } from "./StatusPages.tsx";
import { pathnameChanged, prefetchLoader, type Prefetch } from "./workspace-loaders.ts";

/** Lab work (editing research and advice) is for analysts and above. */
const AnalystOnly = (): ReactNode =>
  useIsAnalyst() ? <Outlet /> : <NoAccessPage needs="analyst" />;

/** The Lab settings and appointing analysts are for admins and above. */
const AdminOnly = (): ReactNode => (useIsAdmin() ? <Outlet /> : <NoAccessPage needs="admin" />);

interface PageRoute {
  lazy: () => Promise<{ Component: ComponentType }>;
  loader?: LoaderFunction;
  shouldRevalidate?: ShouldRevalidateFunction;
}

/**
 * The routes below the /onderzoek gate. Dynamic imports on purpose: route `lazy` keeps
 * the workspace (and recharts) out of the entry bundle, and the router loads a page's
 * chunk and its main queries (loader) in parallel.
 */
export const workspaceRoutes = (queryClient: QueryClient): RouteObject[] => {
  const page = (component: () => Promise<ComponentType>, prefetch?: Prefetch): PageRoute => ({
    lazy: async () => ({ Component: await component() }),
    ...(prefetch && {
      loader: prefetchLoader(queryClient, prefetch),
      shouldRevalidate: pathnameChanged,
    }),
  });

  return [
    {
      lazy: async () => ({ Component: (await import("./WorkspaceLayout.tsx")).WorkspaceLayout }),
      children: [
        {
          index: true,
          ...page(
            async () => (await import("./OverviewPage.tsx")).OverviewPage,
            ({ keys, analyst, load }) => [
              load(samplesQuery(keys)),
              load(researchListQuery(keys)),
              ...(analyst ? [] : [load(adviceListQuery(keys))]),
            ],
          ),
        },
        {
          path: "gebieden",
          ...page(
            async () => (await import("../features/lands/LandsPage.tsx")).LandsPage,
            ({ keys, load }) => [load(landsQuery(keys))],
          ),
        },
        {
          path: "gebieden/nieuw",
          ...page(async () => (await import("../features/lands/LandFormPage.tsx")).LandFormPage),
        },
        {
          path: "gebieden/:landId",
          ...page(
            async () => (await import("../features/lands/LandDetailPage.tsx")).LandDetailPage,
            ({ keys, params, load }) => [load(landQuery(keys, params.landId ?? ""))],
          ),
        },
        {
          path: "gebieden/:landId/bewerken",
          ...page(
            async () => (await import("../features/lands/LandFormPage.tsx")).LandFormPage,
            ({ keys, params, load }) => [load(landQuery(keys, params.landId ?? ""))],
          ),
        },
        {
          path: "monsters",
          ...page(
            async () => (await import("../features/samples/SamplesPage.tsx")).SamplesPage,
            ({ keys, load }) => [load(samplesQuery(keys))],
          ),
        },
        {
          path: "monsters/nieuw",
          ...page(
            async () => (await import("../features/samples/SampleFormPage.tsx")).SampleFormPage,
            ({ keys, load }) => [load(landsQuery(keys))],
          ),
        },
        {
          path: "monsters/:sampleId",
          ...page(
            async () => (await import("../features/samples/SampleDetailPage.tsx")).SampleDetailPage,
            ({ keys, params, load }) => [load(sampleQuery(keys, params.sampleId ?? ""))],
          ),
        },
        {
          path: "monsters/:sampleId/bewerken",
          ...page(
            async () => (await import("../features/samples/SampleFormPage.tsx")).SampleFormPage,
            ({ keys, params, load }) => [load(sampleQuery(keys, params.sampleId ?? ""))],
          ),
        },
        {
          path: "onderzoeken",
          ...page(
            async () =>
              (await import("../features/research/ResearchListPage.tsx")).ResearchListPage,
            ({ keys, load }) => [load(researchListQuery(keys))],
          ),
        },
        {
          path: "onderzoeken/:researchId",
          ...page(
            async () =>
              (await import("../features/research/ResearchDetailPage.tsx")).ResearchDetailPage,
            ({ keys, params, load }) => [load(researchQuery(keys, params.researchId ?? ""))],
          ),
        },
        {
          path: "adviezen",
          ...page(
            async () => (await import("../features/advice/AdviceListPage.tsx")).AdviceListPage,
            ({ keys, load }) => [load(adviceListQuery(keys))],
          ),
        },
        {
          path: "adviezen/:adviceId",
          ...page(
            async () => (await import("../features/advice/AdviceDetailPage.tsx")).AdviceDetailPage,
            ({ keys, params, load }) => [load(adviceQuery(keys, params.adviceId ?? ""))],
          ),
        },
        {
          element: <AnalystOnly />,
          // Others get NoAccessPage from the guard, so their loaders fetch nothing.
          children: [
            {
              path: "referentie",
              ...page(
                async () => (await import("../features/reference/ReferencePage.tsx")).ReferencePage,
              ),
            },
            {
              path: "onderzoeken/:researchId/bewerken",
              ...page(
                async () =>
                  (await import("../features/research/ResearchEditorPage.tsx")).ResearchEditorPage,
                ({ keys, params, analyst, load }) =>
                  analyst ? [load(researchQuery(keys, params.researchId ?? ""))] : [],
              ),
            },
            {
              path: "adviezen/:adviceId/bewerken",
              ...page(
                async () =>
                  (await import("../features/advice/AdviceEditorPage.tsx")).AdviceEditorPage,
                ({ keys, params, analyst, load }) =>
                  analyst ? [load(adviceQuery(keys, params.adviceId ?? ""))] : [],
              ),
            },
          ],
        },
        {
          element: <AdminOnly />,
          // Others get NoAccessPage from the guard, so their loaders fetch nothing.
          children: [
            {
              path: "lab",
              ...page(
                async () => (await import("../features/lab/LabPage.tsx")).LabPage,
                ({ keys, admin, load }) =>
                  admin
                    ? [
                        load(calibrationsQuery(keys)),
                        load(runnerKeysQuery(keys)),
                        load(pipelineQuery(keys)),
                      ]
                    : [],
              ),
            },
            {
              path: "gebruikers",
              // No prefetch: the kit's role page fetches its list on mount.
              ...page(async () => (await import("../features/users/UsersPage.tsx")).UsersPage),
            },
          ],
        },
        { path: "*", element: <NotFoundPage /> },
      ],
    },
  ];
};
